import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus, Pencil, Trash2, Loader2, CheckCircle2, IndianRupee, ShieldCheck, CalendarClock,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FREQUENCIES = ['monthly', 'quarterly', 'annual', 'one-time'];

const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

// Color band for a deadline row.
const bandFor = (d) => {
  const status = (d.status || 'pending').toLowerCase();
  if (status === 'paid' || status === 'filed') return 'green';
  const today = todayStr();
  if (d.due_date < today) return 'red';        // overdue
  if (d.due_date === today) return 'red';       // due today
  const diff = Math.round((new Date(d.due_date) - new Date(today)) / 86400000);
  if (diff <= 2) return 'amber';
  return 'neutral';
};

const STATUS_PILL = {
  pending: 'bg-slate-100 text-slate-600 border-slate-200',
  filed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

// ---- Mark filed/paid dialog ------------------------------------------------
const MarkDialog = ({ deadline, status, onClose, onDone }) => {
  const [date, setDate] = useState(todayStr());
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const verb = status === 'paid' ? 'Paid to government on' : 'Filed on';

  const submit = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/compliance/deadlines/${deadline.id}/mark`, { status, date, notes });
      toast.success(`${deadline.tax_type} marked ${status}`);
      if (res.data?.generated_next) {
        toast.success(`Next period created: ${res.data.generated_next.period_label}`);
      }
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="mark-deadline-dialog">
        <DialogHeader>
          <DialogTitle>Mark {deadline.tax_type} as {status}?</DialogTitle>
          <DialogDescription>{deadline.period_label} · due {fmtDate(deadline.due_date)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">{verb}</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10" data-testid="mark-deadline-date" />
          </div>
          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Challan no., ARN, remarks…" data-testid="mark-deadline-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="mark-deadline-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mark-deadline-confirm">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Confirm {status}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Add / Edit dialog (admin) ---------------------------------------------
const DeadlineDialog = ({ initial, onClose, onSaved }) => {
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState({
    tax_type: initial?.tax_type || '',
    description: initial?.description || '',
    due_date: initial?.due_date || todayStr(),
    frequency: initial?.frequency || 'one-time',
    period_label: initial?.period_label || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.tax_type.trim()) { toast.error('Tax type is required'); return; }
    if (!form.due_date) { toast.error('Due date is required'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${BACKEND_URL}/api/compliance/deadlines/${initial.id}`, form);
        toast.success('Deadline updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/compliance/deadlines`, form);
        toast.success('Deadline added');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="deadline-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit deadline' : 'Add custom deadline'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Tax type *</Label>
              <Input value={form.tax_type} onChange={(e) => set('tax_type', e.target.value)} placeholder="e.g. GSTR-3B" className="h-10" data-testid="deadline-tax-type" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Due date *</Label>
              <Input type="date" value={form.due_date} onChange={(e) => set('due_date', e.target.value)} className="h-10" data-testid="deadline-due-date" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Period label</Label>
              <Input value={form.period_label} onChange={(e) => set('period_label', e.target.value)} placeholder="e.g. May 2026" className="h-10" data-testid="deadline-period" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => set('frequency', v)}>
                <SelectTrigger className="h-10" data-testid="deadline-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.map((f) => (
                    <SelectItem key={f} value={f} className="capitalize" data-testid={`deadline-freq-${f}`}>{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Description</Label>
            <Input value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Optional note" className="h-10" data-testid="deadline-description" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="deadline-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="deadline-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            {isEdit ? 'Save changes' : 'Add deadline'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TaxCalendar = ({ embedded = false }) => {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [markTarget, setMarkTarget] = useState(null);   // {deadline, status}
  const [editTarget, setEditTarget] = useState(null);   // deadline | {} (new)
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/compliance/deadlines`);
      setDeadlines(Array.isArray(r.data) ? r.data : []);
    } catch {
      toast.error('Failed to load tax calendar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/compliance/deadlines`);
        if (active) setDeadlines(Array.isArray(r.data) ? r.data : []);
      } catch {
        if (active) toast.error('Failed to load tax calendar');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/compliance/deadlines/${deleteTarget.id}`);
      toast.success('Deadline deleted');
      setDeleteTarget(null);
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const filtered = useMemo(() => {
    if (filter === 'all') return deadlines;
    return deadlines.filter((d) => (d.status || 'pending') === filter);
  }, [deadlines, filter]);

  const counts = useMemo(() => ({
    all: deadlines.length,
    pending: deadlines.filter((d) => (d.status || 'pending') === 'pending').length,
    filed: deadlines.filter((d) => d.status === 'filed').length,
    paid: deadlines.filter((d) => d.status === 'paid').length,
  }), [deadlines]);

  return (
    <div className={embedded ? 'space-y-5' : 'p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto'} data-testid="tax-calendar-page">
      {!embedded && (
        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 flex items-center gap-2">
              <ShieldCheck className="h-7 w-7 text-primary-700" /> Tax Calendar
            </h1>
            <p className="text-sm text-slate-500 mt-1">GST &amp; TCS compliance deadlines and reminders.</p>
          </div>
          {isAdmin && (
            <Button onClick={() => setEditTarget({})} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="add-deadline-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Deadline
            </Button>
          )}
        </div>
      )}

      {embedded && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-slate-500 flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary-700" /> GST &amp; TCS compliance deadlines and reminders.</p>
          {isAdmin && (
            <Button onClick={() => setEditTarget({})} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="add-deadline-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Deadline
            </Button>
          )}
        </div>
      )}

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2" data-testid="tax-filter">
        {['all', 'pending', 'filed', 'paid'].map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            data-testid={`tax-filter-${f}`}
            className={`px-3 py-1 rounded-full text-xs font-medium border capitalize transition-colors ${
              filter === f ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300'
            }`}
          >
            {f} <span className="opacity-70">· {counts[f]}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500">No deadlines in this view.</div>
      ) : (
        <div className="rounded-xl border border-slate-200 overflow-hidden bg-white" data-testid="tax-deadlines-list">
          {filtered.map((d, i) => {
            const band = bandFor(d);
            const status = (d.status || 'pending').toLowerCase();
            const stripe = band === 'red' ? 'border-l-rose-500' : band === 'amber' ? 'border-l-amber-500' : band === 'green' ? 'border-l-emerald-500' : 'border-l-slate-200';
            const dueClass = band === 'red' ? 'text-rose-600 font-semibold' : band === 'amber' ? 'text-amber-600 font-semibold' : 'text-slate-600';
            return (
              <div key={d.id} className={`flex items-center gap-3 px-4 py-3 border-l-4 ${stripe} ${i > 0 ? 'border-t border-slate-100' : ''} hover:bg-slate-50`} data-testid={`tax-deadline-row-${d.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-800">{d.tax_type}</p>
                    <Badge className={`${STATUS_PILL[status] || STATUS_PILL.pending} border text-[10px] capitalize`} data-testid={`tax-status-${d.id}`}>{status}</Badge>
                    {d.period_label && <span className="text-xs text-slate-400">{d.period_label}</span>}
                  </div>
                  <p className={`text-xs mt-0.5 ${dueClass}`}>
                    Due {fmtDate(d.due_date)}
                    {band === 'red' && status === 'pending' && (d.due_date < todayStr() ? ' · OVERDUE' : ' · due today')}
                    {band === 'amber' && status === 'pending' && ' · due soon'}
                    {(status === 'paid' || status === 'filed') && d.paid_on && ` · ${status} ${fmtDate(d.paid_on)}`}
                    {d.filed_by && (status === 'paid' || status === 'filed') && ` · by ${d.filed_by}`}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {status === 'pending' ? (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => setMarkTarget({ deadline: d, status: 'filed' })} className="text-emerald-600 hover:text-emerald-700 h-8" data-testid={`mark-filed-${d.id}`} title="Mark filed">
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Filed
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setMarkTarget({ deadline: d, status: 'paid' })} className="text-emerald-700 hover:text-emerald-800 h-8" data-testid={`mark-paid-${d.id}`} title="Mark paid">
                        <IndianRupee className="h-4 w-4 mr-1" /> Paid
                      </Button>
                    </>
                  ) : (
                    <span className="text-xs text-emerald-600 font-medium flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Done</span>
                  )}
                  {isAdmin && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => setEditTarget(d)} className="text-slate-500 hover:text-teal-600 h-8 w-8 p-0" data-testid={`edit-deadline-${d.id}`} title="Edit"><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(d)} className="text-slate-500 hover:text-rose-600 h-8 w-8 p-0" data-testid={`delete-deadline-${d.id}`} title="Delete"><Trash2 className="h-4 w-4" /></Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {markTarget && (
        <MarkDialog deadline={markTarget.deadline} status={markTarget.status} onClose={() => setMarkTarget(null)} onDone={load} />
      )}
      {editTarget && (
        <DeadlineDialog initial={editTarget.id ? editTarget : null} onClose={() => setEditTarget(null)} onSaved={load} />
      )}
      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-deadline-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.tax_type} ({deleteTarget?.period_label})?</AlertDialogTitle>
            <AlertDialogDescription>This deadline will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-deadline-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-deadline-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TaxCalendar;
